import { useState, useRef, useEffect } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Briefcase,
  Users,
  BarChart3,
  Settings,
  LogOut,
  Shield,
  Cpu,
  UserX,
  Brain,
  Layers,
  ChevronDown,
  Search,
  Menu,
  X,
} from 'lucide-react';
import { useDeadLetterStats, useHealthCheck } from '../hooks/useApi';
import { useAuth } from '../hooks/useAuth';
import { ArbieMascot } from './ArbieMascot';
import ThemeToggle from './ThemeToggle';
import { Avatar } from './ui';

const recruiterNav = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
  { name: 'Jobs', href: '/jobs', icon: Briefcase },
  { name: 'People', href: '/people', icon: Users },
];

const adminNav = [
  { name: 'Reports', href: '/reports', icon: BarChart3 },
  { name: 'Data Retention', href: '/data-retention', icon: Shield },
  { name: 'GDPR Erasure', href: '/gdpr-erasure', icon: UserX, sub: true },
  { name: 'AI Learning', href: '/learning', icon: Brain },
  { name: 'Settings', href: '/settings', icon: Settings, badgeKey: 'dlq' as const },
  { name: 'AI Config', href: '/settings/ai-config', icon: Cpu, sub: true },
];

const BULL_BOARD_URL = '/api/admin/queues';

export default function TopNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: health, isError } = useHealthCheck();
  const { data: dlqStats } = useDeadLetterStats();
  const { user, logout, isSuperAdmin } = useAuth();

  const [showAdminMenu, setShowAdminMenu] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [showMobileAdmin, setShowMobileAdmin] = useState(false);

  const adminMenuRef = useRef<HTMLDivElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);

  const unresolvedCount = dlqStats?.byStatus?.pending ?? 0;
  const isAdminRoute = adminNav.some(
    (item) => location.pathname === item.href || location.pathname.startsWith(item.href + '/')
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (adminMenuRef.current && !adminMenuRef.current.contains(event.target as Node)) {
        setShowAdminMenu(false);
      }
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setShowMobileMenu(false);
  }, [location.pathname]);

  const handleLogout = async () => {
    setShowUserMenu(false);
    setShowMobileMenu(false);
    await logout();
    navigate('/login');
  };

  return (
    <>
      <header className="relative z-30 flex h-14 flex-shrink-0 items-center justify-between border-b border-surface-200 bg-white px-4 dark:border-gray-700 dark:bg-gray-900 md:px-6">
        {/* Left: Logo */}
        <div className="flex flex-shrink-0 items-center gap-2">
          <ArbieMascot size="md" />
          <div className="flex flex-col leading-none">
            <span className="text-sm font-bold text-gray-900 dark:text-white">Recruiter</span>
            <span className="text-sm font-bold text-accent-500">Buddy</span>
          </div>
        </div>

        {/* Center: Recruiter nav tabs (desktop) */}
        <nav className="absolute left-1/2 hidden h-full -translate-x-1/2 items-stretch lg:flex">
          {recruiterNav.map((item) => (
            <NavLink
              key={item.name}
              to={item.href}
              className={({ isActive }) =>
                `flex items-center gap-2 border-b-2 px-5 text-sm font-medium transition-colors ${
                  isActive
                    ? 'border-accent-500 text-accent-600 dark:text-accent-400'
                    : 'border-transparent text-surface-600 hover:text-surface-900 dark:text-gray-400 dark:hover:text-gray-200'
                }`
              }
            >
              <item.icon className="h-4 w-4" />
              {item.name}
            </NavLink>
          ))}
          {isSuperAdmin && (
            <NavLink
              to="/admin"
              className={({ isActive }) =>
                `flex items-center gap-2 border-b-2 px-5 text-sm font-medium transition-colors ${
                  isActive
                    ? 'border-accent-500 text-accent-600 dark:text-accent-400'
                    : 'border-transparent text-surface-600 hover:text-surface-900 dark:text-gray-400 dark:hover:text-gray-200'
                }`
              }
            >
              Admin
            </NavLink>
          )}
        </nav>

        {/* Right: Utilities */}
        <div className="flex items-center gap-1 md:gap-2">
          {/* Search (desktop) */}
          <div className="relative hidden md:block">
            <Search className="absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-surface-400 dark:text-gray-500" />
            <input
              type="text"
              placeholder="Search people…"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  const query = (e.target as HTMLInputElement).value.trim();
                  if (query) {
                    navigate(`/people?search=${encodeURIComponent(query)}`);
                    (e.target as HTMLInputElement).value = '';
                    (e.target as HTMLInputElement).blur();
                  }
                }
              }}
              className="w-52 rounded-md border border-surface-200 bg-surface-50 py-1.5 pl-8 pr-3 text-sm focus:border-accent-400 focus:outline-none focus:ring-1 focus:ring-accent-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder-gray-500"
            />
          </div>

          {/* Health indicator */}
          <div className="flex items-center gap-1.5 px-1">
            <span
              className={`h-2 w-2 flex-shrink-0 rounded-full ${
                isError ? 'bg-red-500' : health ? 'bg-emerald-500' : 'bg-amber-400'
              }`}
            />
            <span className="hidden text-xs text-surface-500 dark:text-gray-400 sm:inline">
              {isError ? 'Disconnected' : health ? 'Connected' : 'Connecting…'}
            </span>
          </div>

          {/* Theme toggle */}
          <ThemeToggle />

          {/* Admin dropdown (desktop) */}
          <div className="relative hidden lg:block" ref={adminMenuRef}>
            <button
              type="button"
              onClick={() => setShowAdminMenu(!showAdminMenu)}
              className={`flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                isAdminRoute || showAdminMenu
                  ? 'bg-surface-100 text-surface-900 dark:bg-gray-800 dark:text-gray-100'
                  : 'text-surface-600 hover:bg-surface-100 hover:text-surface-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-200'
              }`}
            >
              <Settings className="h-4 w-4" />
              <span>Admin</span>
              {unresolvedCount > 0 && (
                <span className="inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                  {unresolvedCount}
                </span>
              )}
              <ChevronDown
                className={`h-3.5 w-3.5 transition-transform ${showAdminMenu ? 'rotate-180' : ''}`}
              />
            </button>

            {showAdminMenu && (
              <div className="absolute right-0 z-50 mt-1.5 w-52 rounded-lg bg-white py-1 shadow-lg ring-1 ring-black/5 dark:bg-gray-800 dark:ring-gray-700">
                {adminNav.map((item) => (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    onClick={() => setShowAdminMenu(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 px-4 py-2 text-sm transition-colors ${
                        item.sub ? 'pl-9 text-xs' : ''
                      } ${
                        isActive
                          ? 'bg-surface-100 text-surface-900 dark:bg-gray-700 dark:text-gray-100'
                          : 'text-surface-700 hover:bg-surface-50 dark:text-gray-300 dark:hover:bg-gray-700'
                      }`
                    }
                  >
                    <item.icon className={item.sub ? 'h-3.5 w-3.5' : 'h-4 w-4'} />
                    <span className="flex-1">{item.name}</span>
                    {item.badgeKey === 'dlq' && unresolvedCount > 0 && (
                      <span className="inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                        {unresolvedCount}
                      </span>
                    )}
                  </NavLink>
                ))}
                <div className="my-1 border-t border-surface-100 dark:border-gray-700" />
                <a
                  href={BULL_BOARD_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setShowAdminMenu(false)}
                  className="flex items-center gap-2.5 px-4 py-2 text-sm text-surface-700 transition-colors hover:bg-surface-50 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  <Layers className="h-4 w-4" />
                  <span>Queues</span>
                </a>
              </div>
            )}
          </div>

          {/* User dropdown (desktop/tablet) */}
          <div className="relative hidden sm:block" ref={userMenuRef}>
            <button
              type="button"
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-1 rounded-md p-1 transition-colors hover:bg-surface-100 dark:hover:bg-gray-800"
            >
              <Avatar name={user?.name} size="sm" />
              <ChevronDown
                className={`h-3.5 w-3.5 text-surface-400 dark:text-gray-500 transition-transform ${showUserMenu ? 'rotate-180' : ''}`}
              />
            </button>

            {showUserMenu && (
              <div className="absolute right-0 z-50 mt-1.5 w-56 rounded-lg bg-white shadow-lg ring-1 ring-black/5 dark:bg-gray-800 dark:ring-gray-700">
                {user && (
                  <div className="border-b border-surface-100 px-4 py-3 dark:border-gray-700">
                    <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                      {user.name}
                    </p>
                    <p className="truncate text-xs text-surface-500 dark:text-gray-400">{user.email}</p>
                    <p className="mt-0.5 text-xs capitalize text-surface-400 dark:text-gray-500">
                      {user.role}
                    </p>
                    {user.organizationName && (
                      <p className="truncate text-xs text-gray-400 dark:text-gray-500">
                        {user.organizationName}
                      </p>
                    )}
                    {isSuperAdmin && (
                      <p className="truncate text-xs text-gray-400 dark:text-gray-500">
                        Platform Admin
                      </p>
                    )}
                  </div>
                )}
                <div className="py-1">
                  <button
                    onClick={handleLogout}
                    className="flex w-full items-center gap-2.5 px-4 py-2 text-sm text-surface-700 transition-colors hover:bg-surface-50 dark:text-gray-300 dark:hover:bg-gray-700"
                  >
                    <LogOut className="h-4 w-4" />
                    Sign out
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            type="button"
            onClick={() => setShowMobileMenu(!showMobileMenu)}
            className="rounded-md p-2 text-surface-600 transition-colors hover:bg-surface-100 hover:text-surface-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-gray-100 lg:hidden"
          >
            {showMobileMenu ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </header>

      {/* Mobile drawer */}
      {showMobileMenu && (
        <div
          className="fixed inset-0 z-20 pt-14 lg:hidden"
          onClick={() => setShowMobileMenu(false)}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 bg-gray-900/30 dark:bg-black/50" />

          {/* Panel */}
          <div
            className="relative bg-white shadow-xl dark:bg-gray-900"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Mobile search */}
            <div className="border-b border-surface-100 px-4 py-3 dark:border-gray-800">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-surface-400 dark:text-gray-500" />
                <input
                  type="text"
                  placeholder="Search people…"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const query = (e.target as HTMLInputElement).value.trim();
                      if (query) {
                        navigate(`/people?search=${encodeURIComponent(query)}`);
                        setShowMobileMenu(false);
                      }
                    }
                  }}
                  className="w-full rounded-md border border-surface-200 bg-surface-50 py-2 pl-10 pr-4 text-sm focus:border-accent-400 focus:outline-none focus:ring-1 focus:ring-accent-400 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100 dark:placeholder-gray-500"
                />
              </div>
            </div>

            {/* Recruiter nav */}
            <div className="px-3 py-2">
              {recruiterNav.map((item) => (
                <NavLink
                  key={item.name}
                  to={item.href}
                  onClick={() => setShowMobileMenu(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
                      isActive
                        ? 'bg-accent-50 text-accent-700 dark:bg-accent-900/20 dark:text-accent-400'
                        : 'text-surface-700 hover:bg-surface-100 dark:text-gray-300 dark:hover:bg-gray-800'
                    }`
                  }
                >
                  <item.icon className="h-5 w-5" />
                  {item.name}
                </NavLink>
              ))}
            </div>

            {/* Admin section */}
            <div className="border-t border-surface-100 px-3 py-2 dark:border-gray-800">
              <button
                onClick={() => setShowMobileAdmin(!showMobileAdmin)}
                className="flex w-full items-center justify-between px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-surface-500 dark:text-gray-500"
              >
                <span>Admin</span>
                <ChevronDown
                  className={`h-3.5 w-3.5 transition-transform ${
                    showMobileAdmin || isAdminRoute ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {(showMobileAdmin || isAdminRoute) && (
                <div className="mt-1">
                  {adminNav.map((item) => (
                    <NavLink
                      key={item.name}
                      to={item.href}
                      onClick={() => setShowMobileMenu(false)}
                      className={({ isActive }) =>
                        `flex items-center gap-3 rounded-md px-3 py-2 transition-colors ${
                          item.sub ? 'ml-4 text-xs' : 'text-sm font-medium'
                        } ${
                          isActive
                            ? 'bg-surface-100 text-surface-900 dark:bg-gray-700 dark:text-gray-100'
                            : 'text-surface-600 hover:bg-surface-100 dark:text-gray-400 dark:hover:bg-gray-700'
                        }`
                      }
                    >
                      <item.icon className={item.sub ? 'h-4 w-4' : 'h-5 w-5'} />
                      <span className="flex-1">{item.name}</span>
                      {item.badgeKey === 'dlq' && unresolvedCount > 0 && (
                        <span className="inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                          {unresolvedCount}
                        </span>
                      )}
                    </NavLink>
                  ))}
                  <a
                    href={BULL_BOARD_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setShowMobileMenu(false)}
                    className="flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-surface-600 transition-colors hover:bg-surface-100 dark:text-gray-400 dark:hover:bg-gray-700"
                  >
                    <Layers className="h-5 w-5" />
                    <span>Queues</span>
                  </a>
                </div>
              )}
            </div>

            {/* User + sign out */}
            {user && (
              <div className="border-t border-surface-100 px-4 py-3 dark:border-gray-800">
                <div className="flex items-center gap-3">
                  <Avatar name={user.name} size="sm" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">
                      {user.name}
                    </p>
                    <p className="truncate text-xs text-surface-500 dark:text-gray-400">{user.email}</p>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-1.5 rounded-md px-2 py-1.5 text-sm text-surface-600 transition-colors hover:bg-surface-100 dark:text-gray-400 dark:hover:bg-gray-700"
                  >
                    <LogOut className="h-4 w-4" />
                    <span className="sr-only">Sign out</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
